/**
 * AG Grid configuration for GL Key Management
 * Column definitions, cell renderers, and grid options
 */

import React, { useState } from 'react';
import { IconButton, Box, Select, MenuItem, Menu, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

/**
 * Dropdown cell renderer - shows a select dropdown that's always visible
 */
export const DropdownCellRenderer = (props) => {
  const { value, data, colDef, context } = props;
  
  const handleChange = (event) => {
    const newValue = event.target.value;

    // Read colDef, node, api from props at call time — they can be stale if captured at render
    const { colDef, node, api, data: currentData, context: currentContext } = props;

    if (!colDef || !node || !api) return;

    // Use AG Grid API to update the value so onCellValueChanged fires reliably
    node.setDataValue(colDef.field, newValue);

    // For entity/department columns, also update the corresponding ID field
    if (colDef.field === 'entity' && currentContext?.entities) {
      const selectedEntity = currentContext.entities.find(
        e => (e.entity_name || e.entity || '') === newValue
      );
      if (selectedEntity) {
        node.setDataValue('entity_id', selectedEntity.entity_id);
      }
    } else if (colDef.field === 'department_name' && currentContext?.departments) {
      const selectedDept = currentContext.departments.find(
        d => String(d.entityId) === String(currentData.entity_id) && d.departmentName === newValue
      );
      if (selectedDept) {
        node.setDataValue('department_id', selectedDept.departmentId);
      }
    }

    // Refresh the cell to update any styling that depends on the value.
    api.refreshCells({ rowNodes: [node], force: true });
  };
  
  // Guard: AG Grid can call this renderer for non-data rows (group/overlay); skip those.
  if (!data) return null;

  // Get options based on column
  let options = [];
  let isDriverColumn = false;

  // For entity: fall back to looking up entity_name from the entities list if value is blank
  let displayValue = value || '';
  if (colDef.field === 'entity' && !displayValue && context.entities && data.entity_id) {
    const match = context.entities.find(e => String(e.entity_id) === String(data.entity_id));
    displayValue = match?.entity_name || match?.entity || '';
  }
  // For department: fall back to looking up departmentName from departments list if value is blank
  if (colDef.field === 'department_name' && !displayValue && context.departments && data.department_id) {
    const match = context.departments.find(
      d => String(d.entityId) === String(data.entity_id) && String(d.departmentId) === String(data.department_id)
    );
    displayValue = match?.departmentName || '';
  }
  const currentValue = displayValue;
  
  if (colDef.field === 'entity' && context.entities) {
    // Get entity names from entities array - use entity_name property
    options = context.entities.map(e => e.entity_name || e.entity || '');
  } else if (colDef.field === 'department_name' && context.departments) {
    // Get departments for this entity
    options = context.departments
      .filter(d => String(d.entityId) === String(data.entity_id))
      .map(d => d.departmentName);
  } else if (colDef.field === 'parent_account' && context.parentAccounts) {
    options = context.parentAccounts;
  } else if (colDef.field === 'driver' && context.drivers) {
    // Get only Stat accounts from the same entity for use as drivers
    isDriverColumn = true;
    options = context.drivers
      .filter(d => 
        String(d.entity_id) === String(data.entity_id) &&
        d.acct_type === 'Stat'
      );
    
    // If current value exists but is not in options (e.g., old text format or different department),
    // add it as a special option so it can be displayed
    if (currentValue && !options.some(opt => opt.gl_key === currentValue)) {
      // Create a placeholder option for the current value
      options = [
        { gl_key: currentValue, account_name: `(Current: ${currentValue})`, _isLegacy: true },
        ...options
      ];
    }
  } else if (colDef.cellEditorParams?.values) {
    options = colDef.cellEditorParams.values;
  }
  
  // Entity and department are locked once a row already has a GL key to prevent inconsistent state
  // (gl_key is read-only and is not recomputed when its component fields change)
  const isDisabled =
    (colDef.field === 'entity' || colDef.field === 'department_name') &&
    Boolean(data?.gl_key);
  
  // For driver column, always consider current value as valid
  // For other columns, check if value is in options
  const isValidValue = isDriverColumn 
    ? true // Always valid for driver column (we add current value to options if needed)
    : options.includes(currentValue) || currentValue === '';
  
  return (
    <Select
      value={currentValue}
      onChange={handleChange}
      size="small"
      disabled={isDisabled}
      displayEmpty
      sx={{
        width: '100%',
        height: '100%',
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': {
          padding: '4px 8px',
          fontSize: '0.875rem',
          color: !isValidValue ? '#d32f2f' : 'inherit',
        },
      }}
    >
      <MenuItem value="">
        <em>None</em>
      </MenuItem>
      {isDriverColumn ? (
        // Driver column: show "GL Key - Account Name" with bold GL Key
        options.map((option, index) => (
          <MenuItem key={index} value={option.gl_key}>
            {option._isLegacy ? (
              <em style={{ color: '#ff9800' }}>{option.account_name}</em>
            ) : (
              <><strong>{option.gl_key}</strong> - {option.account_name}</>
            )}
          </MenuItem>
        ))
      ) : (
        // Other columns: show simple string values
        options.map((option, index) => (
          <MenuItem key={index} value={option}>
            {option}
          </MenuItem>
        ))
      )}
    </Select>
  );
};

/**
 * Actions cell renderer - shows menu with edit and delete options
 */
export const ActionsCellRenderer = (props) => {
  const { onEditMonthly, onDelete } = props.context || {};
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };
  
  const handleMenuClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setAnchorEl(null);
  };
  
  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    handleMenuClose();
    if (onEditMonthly) {
      onEditMonthly(props.data);
    }
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    handleMenuClose();
    if (onDelete) {
      onDelete(props.data);
    }
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <IconButton
        size="small"
        onClick={handleMenuOpen}
        title="Actions"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit monthly values</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            Delete <strong>{props.data.gl_key}</strong> - {props.data.account_name}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

/**
 * Get column definitions for GL Key grid
 * @param {object} context - Context object with data arrays
 * @returns {Array} Column definitions
 */
export const getColumnDefs = (context) => {
  return [
    {
      headerName: '',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
      lockPosition: true,
      suppressMovable: true,
    },
    {
      headerName: 'GL Key',
      field: 'gl_key',
      editable: false,
      cellStyle: { backgroundColor: '#f5f5f5' },
      width: 140,
      pinned: 'left',
      lockPosition: true,
    },
    {
      headerName: 'Entity',
      field: 'entity',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellStyle: (params) => {
        if (params.data._changed?.entity) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 150,
    },
    {
      headerName: 'Department',
      field: 'department_name',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellStyle: (params) => {
        if (params.data._changed?.department_name) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 150,
    },
    {
      headerName: 'Account Name',
      field: 'account_name',
      editable: true,
      cellEditor: 'agTextCellEditor',
      valueSetter: (params) => {
        // Reject empty account name
        if (!params.newValue) return false;
        // Store the account name as-is without parsing or prefix manipulation
        params.data.account_name = params.newValue;
        return true;
      },
      cellStyle: (params) => {
        if (params.data._changed?.account_name) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 200,
      flex: 1,
    },
    {
      headerName: 'Parent Account',
      field: 'parent_account',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellStyle: (params) => {
        if (params.data._changed?.parent_account) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 150,
    },
    {
      headerName: 'Acct Type',
      field: 'acct_type',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellEditorParams: {
        values: ['Revenue', 'Expense', 'Deduction', 'Other Revenue', 'Asset', 'Liability', 'Equity', 'Stat', 'P&L'],
      },
      cellStyle: (params) => {
        if (params.data._changed?.acct_type) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 130,
    },
    {
      headerName: 'Fixed/Variable',
      field: 'fixed_or_variable',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellEditorParams: {
        values: ['Fixed', 'Variable', 'Stat', ''],
      },
      cellStyle: (params) => {
        if (params.data._changed?.fixed_or_variable) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 130,
    },
    {
      headerName: 'Driver',
      field: 'driver',
      cellRenderer: 'dropdownCellRenderer',
      suppressClickEdit: true,
      editable: false,
      cellStyle: (params) => {
        if (params.data._changed?.driver) return { backgroundColor: '#fff3cd' };
        return null;
      },
      width: 150,
    },
    {
      headerName: 'Actions',
      field: 'actions',
      cellRenderer: 'actionsCellRenderer',
      suppressClickEdit: true,
      editable: false,
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
    },
  ];
};

/**
 * Get default grid options
 * @param {object} handlers - Event handlers
 * @returns {object} Grid options
 */
export const getGridOptions = (handlers) => {
  const {
    onCellValueChanged,
    onRowSelected,
    onGridReady,
  } = handlers;

  return {
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    onCellValueChanged,
    onRowSelected,
    onGridReady,
    getRowStyle: (params) => {
      if (params.data._isNew) return { backgroundColor: '#d1ecf1' }; // New rows (blue)
      if (params.data._hasChanges) return { borderLeft: '3px solid #ffc107' }; // Changed rows (yellow border)
      return null;
    },
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
    },
    animateRows: true,
    enableCellTextSelection: true,
    ensureDomOrder: true,
    pagination: true,
    paginationPageSize: 50,
    paginationPageSizeSelector: [25, 50, 100, 200],
  };
};

/**
 * Get row ID for AG Grid
 * @param {object} params - Row params
 * @returns {string} Row ID
 */
export const getRowId = (params) => {
  return params.data.id ? String(params.data.id) : params.data.gl_key;
};
